import { benefitRows, type BenefitRows } from 'tio-employee/utils/beta/benefit-rows';
import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import Popover from 'tio-ui/components/popover';
import TioWordmark from 'tio-common/components/beta/tio-wordmark';
import { PRIVACY_POLICY, TERMS_OF_SERVICE } from 'tio-common/components/site-footer';
import { LinkTo } from '@ember/routing';
import {
  PhBookOpen,
  PhBookOpenText,
  PhCurrencyCircleDollar,
  PhFileArrowUp,
  PhFirstAidKit,
  PhGear,
  PhGlobe,
  PhGraduationCap,
  PhGridNine,
  PhHandCoins,
  PhHeadset,
  PhHandHeart,
  PhHouse,
  PhLifebuoy,
  PhMegaphone,
  PhCheck,
  PhDotsThreeVertical,
  PhQuestionMark,
  PhRocketLaunch,
  PhScales,
  PhSignOut,
  PhStudent,
  PhTarget,
  PhUserCircle,
  PhUsersThree,
} from 'ember-phosphor-icons';
import type { TOC } from '@ember/component/template-only';
import type IntlService from 'ember-intl/services/intl';
import type RouterService from '@ember/routing/router-service';
import type SessionContextService from 'tio-employee/services/session-context';
import type { Company } from 'tio-data/data/resources/company.type';
import type PreRegisterService from 'tio-employee/services/pre-register';
import type BetaBrandService from 'tio-employee/services/beta-brand';

/**
 * Brand Updates 2026 side navigation.
 *
 * Source: Figma "Brand Updates 2026" → Side Navigation & Footer (section 47-528).
 * Employee frame `4-8566`, partner admin `4-8774`; dark variants `17-2806` and
 * `17-3023`. The annotations are numbered 1–8 in the design; each is quoted at
 * the code that implements it, here or in `styles/beta/side-nav.css`.
 *
 * This renders the **Employee** and **Partner Admin** variants — both live in
 * tio-employee and differ only by which groups the role can see. The third
 * variant (Internal Admin) is tio-admin's nav and is not implemented here; see
 * BETA_SIDE_NAV_GAPS.md (gap: internal-admin).
 *
 * Copy: existing `sidebar.*` keys are reused wherever the design's label matches
 * one. The six labels new to this design — Benefits, Loan Wellness, Loan
 * Repayment, Coaching, FAQs, Account Settings — have their own keys in all three
 * employee locales. They were briefly hardcoded English, which showed es-mx and
 * fr-ca users a half-translated rail.
 */

/**
 * DESTINATIONS ARE CARRIED OVER, NOT REDESIGNED.
 *
 * The Figma frame specifies labels, order and styling — it does not say where
 * anything points. Every row below therefore keeps the destination its previous
 * nav entry had, including Support's Zendesk-chat behaviour.
 *
 * ONE EXCEPTION, isolated here so it is easy to change: "FAQs" is a label with
 * no predecessor and no FAQ route in the app. Pointed at the nearest existing
 * surface pending design's answer.
 */
const FAQS_ROUTE = 'authenticated.questions-you-have.paying-for-college';

/**
 * A row in the rail.
 *
 * Renders a real `<LinkTo>` so the destination behaves like a link — middle-click
 * and cmd-click open a tab, the status bar shows where it goes, and the href is
 * copyable. It was a `<button>` calling `transitionTo`, which looked identical
 * and did none of that.
 *
 * The one exception is a row whose behaviour is an action rather than a
 * destination: Support opens Zendesk chat when the partner has it. That renders a
 * button, because it is one.
 */
interface BetaSideNavLinkSignature {
  Args: {
    /** Phosphor icon component for the row. */
    icon: unknown;
    /** Visible label. Callers pass a translated string. */
    label: string;
    /** In-app route this row goes to, and what the selected state matches on. */
    route: string;
    /**
     * Whether child routes count as selected. Defaults to true, so a section
     * landing stays lit while you are inside it; pass `false` for a row that
     * should only light on an exact match.
     */
    matchSubtree?: boolean;
    /**
     * Makes the row an action instead of a link — today only Support opening
     * Zendesk chat. `@route` is still used for the selected state.
     */
    onClick?: () => void;
  };
  Element: HTMLElement;
}

export class BetaSideNavLink extends Component<BetaSideNavLinkSignature> {
  @service declare router: RouterService;

  /**
   * Selected state. Read off `currentRouteName` rather than `router.isActive`
   * so it is autotracked — `isActive` is a method and does not invalidate on
   * its own when the route changes.
   */
  get isActive(): boolean {
    const current = this.router.currentRouteName;
    const route = this.args.route;

    if (!current || !route) {
      return false;
    }

    if (this.args.matchSubtree === false) {
      return current === route;
    }

    return current === route || current.startsWith(`${route}.`);
  }

  /**
   * Note 2: "Icon: Primary Purple 80, **Bold**". Phosphor's bold is an icon
   * weight, not a font-weight, so it cannot come from the stylesheet the way
   * the fill and text colour do.
   */
  get iconWeight(): 'bold' | 'regular' {
    return this.isActive ? 'bold' : 'regular';
  }

  <template>
    <li>
      {{#if @onClick}}
        <button
          type="button"
          class="tio-beta-nav__link {{if this.isActive 'tio-beta-nav__link--active'}}"
          aria-current={{if this.isActive "page"}}
          {{on "click" @onClick}}
          ...attributes
        >
          <@icon @size="20" @weight={{this.iconWeight}} class="tio-beta-nav__link-icon" />
          <span class="tio-beta-nav__link-label">{{@label}}</span>
        </button>
      {{else}}
        <LinkTo
          @route={{@route}}
          class="tio-beta-nav__link {{if this.isActive 'tio-beta-nav__link--active'}}"
          aria-current={{if this.isActive "page"}}
          ...attributes
        >
          <@icon @size="20" @weight={{this.iconWeight}} class="tio-beta-nav__link-icon" />
          <span class="tio-beta-nav__link-label">{{@label}}</span>
        </LinkTo>
      {{/if}}
    </li>
  </template>
}

interface BetaSideNavGroupSignature {
  Args: { title?: string };
  Blocks: { default: [] };
  Element: HTMLDivElement;
}

export const BetaSideNavGroup: TOC<BetaSideNavGroupSignature> = <template>
  <div class="tio-beta-nav__group" ...attributes>
    {{#if @title}}
      <div class="tio-beta-nav__group-title">{{@title}}</div>
    {{/if}}
    <ul class="tio-beta-nav__items">
      {{yield}}
    </ul>
  </div>
</template>;

interface BetaSideNavCompanySignature {
  Args: {
    company: Company;
    currentId?: string;
    onSelect: (company: Company) => void;
    /** Row class, since the two menu presentations style their rows differently. */
    itemClass: string;
  };
  Element: HTMLButtonElement;
}

class BetaSideNavCompany extends Component<BetaSideNavCompanySignature> {
  get isCurrent(): boolean {
    return this.args.currentId === this.args.company.id;
  }

  select = () => this.args.onSelect(this.args.company);

  <template>
    <button
      type="button"
      class={{@itemClass}}
      data-test-beta-side-nav-company
      {{on "click" this.select}}
      ...attributes
    >
      {{#if this.isCurrent}}
        <PhCheck @size="20" />
      {{else}}
        <span class="tio-beta-nav__menu-spacer"></span>
      {{/if}}
      {{@company.name}}
    </button>
  </template>
}

/**
 * Note 6: "The User component includes a small avatar and the username on
 * file. This element is clickable and opens a small context menu where user
 * profile elements can be revealed. This currently includes: settings, logout."
 *
 * The menu is the **Avatar menu** that ships with the mega menu in Common
 * Components — `MegaMenu/{native|UPS|Advocate}/{lightmode|darkmode}`, one avatar
 * menu per identity and theme. DESIGN.md describes it as "a card with the user's
 * initials, name, and Settings / Help / Logout, closing on a Powered by
 * Tuition.io line above privacy / terms".
 *
 * WHERE THE HANDOVER SCREENSHOTS WIN OVER THAT SENTENCE: they draw **four** rows,
 * not three — Settings, Support, Help, Log out — and the label is "Settings", not
 * the rail's "Account Settings". The screenshots are the newer artefact, so they
 * govern; DESIGN.md's Mega menu paragraph wants updating to match.
 *
 * BEHIND THE BETA OPT-IN. `@useAvatarMenu` follows `betaExperience.isEnabled`,
 * the same three-tier lifecycle the beta dashboard uses: flag on in local, vodka
 * and staging, then opt in from the Profile toggle. With it off this renders the
 * shipped two-item card and Support and Help stay rail rows, so production is
 * untouched until the flag graduates.
 *
 * SUPPORT AND HELP MOVED HERE FROM THE RAIL. Both were rows in the Resources
 * group, and both were a nav link whose destination leaves the app for Zendesk —
 * the wrong affordance for a navigation rail. Neither destination changed in the
 * move: Support still opens Zendesk chat where the partner has it and still falls
 * back to `contact-us` where it does not, and Help still goes to `contact-us`.
 * The site footer keeps its own copy of both, which is what footer note 4 means
 * by the rail being free to drop them.
 *
 * THE ROLE SWITCHER STAYS. Note 6's "currently includes" describes what the menu
 * holds, it does not forbid what is already there, and this is the only way to
 * change role anywhere in the app. It renders only when the user has more than
 * one active company, so a single-role user sees exactly the rows the screenshots
 * draw. See BETA_SIDE_NAV_GAPS.md (gap: role-switch).
 */
interface BetaSideNavUserSignature {
  Args: {
    /** Partner has Zendesk chat, which makes Support an action rather than a link. */
    isZendeskEnabled: boolean;
    /** Opens Zendesk chat. Only called when `isZendeskEnabled`. */
    onOpenChat: () => void;
    /**
     * The partner's configurable line, echoed under the wordmark the way the site
     * footer echoes it. Omitted entirely when absent — the row disappears rather
     * than showing placeholder copy.
     */
    tagline?: string;
    /**
     * Co-brand key. The menu portals to `document.body`, outside the rail, so it
     * cannot inherit the rail's `data-beta-brand` and has to be told.
     */
    brand?: string;
    /**
     * Render the 2026 avatar menu instead of the shipped two-item card. Gated on
     * the beta opt-in, so Support and Help are in exactly one place at a time —
     * see BetaSideNav's `useAvatarMenu`.
     */
    useAvatarMenu: boolean;
  };
}

export class BetaSideNavUser extends Component<BetaSideNavUserSignature> {
  @service declare sessionContext: SessionContextService;
  @service declare router: RouterService;
  @service declare intl: IntlService;

  @tracked isMenuOpen = false;

  get person() {
    return this.sessionContext.user?.person;
  }

  get fullName(): string {
    const person = this.person;

    if (!person) {
      return '';
    }

    return [person.firstName, person.lastName].filter(Boolean).join(' ');
  }

  get initials(): string {
    return this.person?.initials ?? '';
  }

  /** Carried over from the previous menu — see the class comment. */
  get availableCompanies() {
    return this.sessionContext.availableCompanies;
  }

  get hasCompanyChoice(): boolean {
    return !this.sessionContext.isFamilyMember && this.availableCompanies.length > 1;
  }

  get currentCompanyId(): string | undefined {
    return this.sessionContext.currentCompany?.id ?? undefined;
  }

  /** Same string the site footer closes on, from the same key. */
  get copyright(): string {
    return this.intl.t('site_footer.copyright', { year: new Date().getFullYear() });
  }

  setCompany = (company: Company) => {
    this.isMenuOpen = false;
    this.sessionContext.setCurrentCompany(company);
  };

  toggleMenu = () => {
    this.isMenuOpen = !this.isMenuOpen;
  };

  closeMenu = () => {
    this.isMenuOpen = false;
  };

  goTo = (route: string) => {
    this.isMenuOpen = false;
    this.router.transitionTo(route);
  };

  goToSettings = () => this.goTo('authenticated.profile');
  goToHelp = () => this.goTo('authenticated.contact-us');
  goToLogout = () => this.goTo('logout');

  /** Support's non-Zendesk destination, unchanged from the rail row it replaces. */
  goToSupport = () => this.goTo('authenticated.contact-us');

  /** Closes the menu so it is not left hanging over the chat widget it opens. */
  openChat = () => {
    this.isMenuOpen = false;
    this.args.onOpenChat();
  };

  <template>
    <div class="tio-beta-nav__user">
      <Popover
        @isOpen={{this.isMenuOpen}}
        @onClose={{this.closeMenu}}
        {{! The avatar menu opens beside the rail: anchored above the pill it is
            wider than the 240px rail and covers the nav rows behind it. The
            shipped card is small enough to keep its original placement. }}
        @placement={{if @useAvatarMenu "right-end" "top"}}
        @showCloseButton={{@useAvatarMenu}}
        @maxWidth={{if @useAvatarMenu 280 360}}
        class="tio-beta-nav__user-popover
          {{if @useAvatarMenu 'tio-beta-nav__user-popover--avatar'}}"
        data-beta-brand={{@brand}}
      >
        <:trigger as |p|>
          <button
            type="button"
            class="tio-beta-nav__user-pill"
            aria-haspopup="menu"
            aria-expanded={{if this.isMenuOpen "true" "false"}}
            data-test-beta-side-nav-user
            {{p.register}}
            {{on "click" this.toggleMenu}}
          >
            <span class="tio-beta-nav__user-avatar" aria-hidden="true">{{this.initials}}</span>
            <span class="tio-beta-nav__user-name">{{this.fullName}}</span>
            {{#if @useAvatarMenu}}
              {{! Decorative: the button already announces the name and its popup. }}
              <PhDotsThreeVertical
                @size="16"
                class="tio-beta-nav__user-more"
                aria-hidden="true"
                data-test-beta-side-nav-user-more
              />
            {{/if}}
          </button>
        </:trigger>
        <:body>
          {{! Deliberately NOT role="menu": that pattern promises managed focus and
              Arrow/Home/End handling, and Popover only handles dismissal. As a
              plain group of buttons it keeps normal tab order, which is honest
              about what it supports. }}
          {{#if @useAvatarMenu}}
            <div class="tio-beta-nav__avatar-menu" data-test-beta-side-nav-avatar-menu>

              {{! The screenshots open on the identity: avatar over name, centred,
                  both a size up from the pill that triggered the menu. }}
              <div class="tio-beta-nav__avatar-menu-identity">
                <span class="tio-beta-nav__avatar-menu-avatar" aria-hidden="true">
                  {{this.initials}}
                </span>
                <span class="tio-beta-nav__avatar-menu-name">{{this.fullName}}</span>
              </div>

              <div class="tio-beta-nav__avatar-menu-items">
                <button
                  type="button"
                  class="tio-beta-nav__avatar-menu-item"
                  data-test-beta-side-nav-account-settings
                  {{on "click" this.goToSettings}}
                >
                  <PhGear @size="20" />
                  {{t "sidebar.settings"}}
                </button>

                {{#if this.hasCompanyChoice}}
                  {{#each this.availableCompanies as |company|}}
                    <BetaSideNavCompany
                      @company={{company}}
                      @currentId={{this.currentCompanyId}}
                      @onSelect={{this.setCompany}}
                      @itemClass="tio-beta-nav__avatar-menu-item"
                    />
                  {{/each}}
                {{/if}}

                {{#if @isZendeskEnabled}}
                  <button
                    type="button"
                    class="tio-beta-nav__avatar-menu-item"
                    data-test-beta-side-nav-support
                    {{on "click" this.openChat}}
                  >
                    <PhHeadset @size="20" />
                    {{t "sidebar.support"}}
                  </button>
                {{else}}
                  <button
                    type="button"
                    class="tio-beta-nav__avatar-menu-item"
                    data-test-beta-side-nav-support
                    {{on "click" this.goToSupport}}
                  >
                    <PhHeadset @size="20" />
                    {{t "sidebar.support"}}
                  </button>
                {{/if}}

                <button
                  type="button"
                  class="tio-beta-nav__avatar-menu-item"
                  data-test-beta-side-nav-help
                  {{on "click" this.goToHelp}}
                >
                  <PhQuestionMark @size="20" />
                  {{t "sidebar.help"}}
                </button>

                <button
                  type="button"
                  class="tio-beta-nav__avatar-menu-item"
                  data-test-beta-side-nav-logout
                  {{on "click" this.goToLogout}}
                >
                  <PhSignOut @size="20" />
                  {{t "header.user_menu.logout"}}
                </button>
              </div>

              {{! The closing block the screenshots draw, and the same four strings
                  the site footer carries — but reachable from every route, where
                  the footer itself only renders on the dashboard and profile
                  surfaces (open question 8d, issue #10679). }}
              <div class="tio-beta-nav__avatar-menu-footer">
                <p class="tio-beta-nav__avatar-menu-powered">
                  <span>{{t "site_footer.powered_by"}}</span>
                  <TioWordmark class="tio-beta-nav__avatar-menu-wordmark" />
                </p>

                {{#if @tagline}}
                  <p class="tio-beta-nav__avatar-menu-tagline">{{@tagline}}</p>
                {{/if}}

                <p class="tio-beta-nav__avatar-menu-copyright">{{this.copyright}}</p>

                <p class="tio-beta-nav__avatar-menu-legal">
                  <a
                    href={{PRIVACY_POLICY}}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-test-beta-side-nav-privacy
                  >{{t "sidebar.privacy_policy"}}</a>
                  <a
                    href={{TERMS_OF_SERVICE}}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-test-beta-side-nav-terms
                  >{{t "common.terms_and_conditions"}}</a>
                </p>
              </div>
            </div>
          {{else}}
            {{! The shipped menu, unchanged. Support and Help are rail rows in this
                branch, so the menu holds what note 6 names plus the switcher. }}
            <div class="tio-beta-nav__user-menu">
              <button
                type="button"
                class="tio-beta-nav__user-menu-item"
                data-test-beta-side-nav-account-settings
                {{on "click" this.goToSettings}}
              >
                <PhGear @size="20" />
                {{t "sidebar.account_settings"}}
              </button>
              {{#if this.hasCompanyChoice}}
                {{#each this.availableCompanies as |company|}}
                  <BetaSideNavCompany
                    @company={{company}}
                    @currentId={{this.currentCompanyId}}
                    @onSelect={{this.setCompany}}
                    @itemClass="tio-beta-nav__user-menu-item"
                  />
                {{/each}}
              {{/if}}
              <button
                type="button"
                class="tio-beta-nav__user-menu-item"
                data-test-beta-side-nav-logout
                {{on "click" this.goToLogout}}
              >
                <PhSignOut @size="20" />
                {{t "header.user_menu.logout"}}
              </button>
            </div>
          {{/if}}
        </:body>
      </Popover>
    </div>
  </template>
}

export interface BetaSideNavSignature {
  Args: {
    // ── Who the viewer is ─────────────────────────────────────────────────
    // These four overlap deliberately: a person can hold several at once, and
    // several rows are gated on a combination. `isTaOnly` is not a role but a
    // partner shape — a company whose only benefit is tuition assistance.

    /** Partner offers tuition assistance only, so the rail hides loan rows. */
    isTaOnly: boolean;
    /** Viewer is an enrolled employee, the audience for the Benefits group. */
    isParticipant: boolean;
    /** Viewer is a participant's family member; sees a narrower Benefits set. */
    isFamilyMember: boolean;

    // ── Which benefits the partner has ────────────────────────────────────
    // Partner-level switches. A benefit row needs BOTH its switch here and an
    // audience above — see `showLearningPartners` for why the pairing matters.

    /** Company has tuition assistance launched (`company.taEnabled`). */
    showTuitionAssistance: boolean;
    /** Show Loan Wellness. False for a D-5 wellness train. */
    showWellness: boolean;
    /** Company has PSLF and the employee has no rogue FEIN. */
    showPslf: boolean;
    /** Company has Secure Your Future (401k match). */
    showSyf: boolean;
    /** Company has income-driven repayment. */
    showIdr: boolean;
    /** Employee has a contribution plan whose cadence is not NEVER. */
    showSlr: boolean;
    /** Company has Learning Partners enabled (`companySettings.lpn`). */
    isLpnEnabled: boolean;

    // ── Administration — note 8 ───────────────────────────────────────────
    // Also overlapping. `isAdmin` is Tuition.io staff and outranks the rest;
    // the others are partner-side and are usually paired with a benefit switch
    // (an owner only sees the TA admin rows if the partner actually has TA).

    /** Tuition.io internal staff (TIO_ADMIN). Sees every Administration row. */
    isAdmin: boolean;
    /** Partner ACCOUNT_OWNER. */
    isOwner: boolean;
    /** Partner PSLF_ADMIN; sees Admin PSLF without needing owner. */
    isPslfAdmin: boolean;
    /** Partner EMPLOYEE_ADMIN. */
    isEmployeeAdmin: boolean;
    /** Holds any `TAS.Approver*` role; sees the approval queue. */
    isTasApprover: boolean;
    /** `taPartnerPaymentsReviewPage` feature flag, for the TA Payments row. */
    isTasPartnerPaymentsEnabled: boolean;

    // ── Support ───────────────────────────────────────────────────────────

    /** Partner has Zendesk chat, which makes Support an action, not a link. */
    isZendeskEnabled: boolean;
    /** Opens Zendesk chat. Only called when `isZendeskEnabled`. */
    onOpenChat: () => void;

    // ── Avatar menu ───────────────────────────────────────────────────────

    /** The partner's configurable line, resolved once for the menu and the footer. */
    tagline?: string;
    /** The beta opt-in. One switch, so the two rows are never in both places or neither. */
    useAvatarMenu: boolean;
  };
  Element: HTMLElement;
}

export default class BetaSideNav extends Component<BetaSideNavSignature> {
  @service declare preRegister: PreRegisterService;
  @service declare betaBrand: BetaBrandService;

  /** Set when the partner's logo asset fails to load, so the slot renders empty. */
  @tracked private logoFailed = false;

  /**
   * Note 1: "We are moving the Partner logo out of the header and into our
   * side navigation... we should limit Partner logos to 40px height."
   * The cap is enforced in CSS so it holds for any asset a partner uploads.
   */
  get partnerLogoUrl(): string | null {
    return this.preRegister.logo ?? null;
  }

  /**
   * A configured URL is not a loadable asset — a partner's logo can 404 or be
   * withdrawn. Note 1's empty slot is the intended logo-less state, so falling
   * back to it beats leaving the browser's broken-image icon in the rail.
   */
  get showPartnerLogo(): boolean {
    return Boolean(this.partnerLogoUrl) && !this.logoFailed;
  }

  handleLogoError = () => {
    this.logoFailed = true;
  };

  get partnerName(): string {
    return this.preRegister.companyName ?? 'Tuition.io';
  }

  get brand(): string {
    return this.betaBrand.brand;
  }

  // ── Administration gates, copied verbatim from the previous rail ────────
  get showAdministration(): boolean {
    const { isAdmin, isOwner, isPslfAdmin, isTasApprover, isEmployeeAdmin } = this.args;

    return Boolean(isAdmin || isOwner || isPslfAdmin || isTasApprover || isEmployeeAdmin);
  }

  get showAdminCore(): boolean {
    return Boolean(this.args.isAdmin || this.args.isOwner);
  }

  get showAdminPslf(): boolean {
    const { isAdmin, isOwner, showPslf, isPslfAdmin } = this.args;

    return Boolean(isAdmin || (isOwner && showPslf) || isPslfAdmin);
  }

  get showAdminTas(): boolean {
    const { isAdmin, isOwner, isEmployeeAdmin, showTuitionAssistance, isTasApprover } = this.args;

    return Boolean(
      isAdmin || ((isOwner || isEmployeeAdmin) && showTuitionAssistance) || isTasApprover
    );
  }

  /**
   * The legacy rail nested this row inside the TA-admin group, so that group's
   * gate applied on top of the payments flag. Flattening the rail dropped it,
   * which showed a TA destination to an employee admin at a partner with TA
   * switched off. `showAdminTas` is re-applied to restore the old audience.
   */
  get showAdminTasPayments(): boolean {
    const { isTasPartnerPaymentsEnabled, isAdmin, isOwner, isEmployeeAdmin } = this.args;

    return Boolean(
      this.showAdminTas && isTasPartnerPaymentsEnabled && (isAdmin || isOwner || isEmployeeAdmin)
    );
  }

  /**
   * Learning Partners was only ever a member-facing row — the legacy rail put it
   * inside the TA-only branch and the participant/family branch, never in an
   * admin one. Gating on the company flag alone showed it to admin-only logins.
   */
  get showLearningPartners(): boolean {
    return this.benefitRows.learningPartners;
  }

  get showAllReports(): boolean {
    return Boolean(this.args.isAdmin || this.args.isOwner || this.args.isEmployeeAdmin);
  }

  // ── Benefit gates ───────────────────────────────────────────────────────
  get benefitRows(): BenefitRows {
    return benefitRows(this.args);
  }

  get isTaOnlyMember(): boolean {
    return this.benefitRows.isTaOnlyMember;
  }

  get isMember(): boolean {
    return this.benefitRows.isMember;
  }

  /** The previous rail hid these from family members. */
  get showMemberOnly(): boolean {
    return this.benefitRows.showMemberOnly;
  }

  // One getter per design-named row, so the template reads in design order.
  get showTuitionAssistanceRow(): boolean {
    return this.benefitRows.tuitionAssistance;
  }

  get showWellnessRow(): boolean {
    return this.benefitRows.wellness;
  }

  get showPslfRow(): boolean {
    return this.benefitRows.pslf;
  }

  get showSlrRow(): boolean {
    return this.benefitRows.slr;
  }

  get showSyfRow(): boolean {
    return this.benefitRows.syf;
  }

  get showIdrRow(): boolean {
    return this.benefitRows.idr;
  }

  <template>
    <nav
      class="tio-beta-nav"
      data-beta-brand={{this.brand}}
      aria-label={{t "sidebar.home"}}
      data-test-beta-side-nav
      ...attributes
    >
      <div class="tio-beta-nav__top">

        {{! ── Note 1 — partner logo, capped at 40px high ──
             The Tuition.io mark used to stand in here when a partner had no
             logo. Footer note 1 moves that mark to the footer ("out of the side
             nav and into our new footer component"), so the slot now shows the
             partner logo or nothing. Its 88px height is on the container, so a
             logo-less partner gets the same spacing rather than a shifted rail.
             Whether that gap wants filling is a question for design. }}
        <div class="tio-beta-nav__logo">
          {{#if this.showPartnerLogo}}
            <img
              src={{this.partnerLogoUrl}}
              alt={{this.partnerName}}
              class="tio-beta-nav__logo-img"
              {{on "error" this.handleLogoError}}
              data-test-beta-side-nav-logo
            />
          {{/if}}
        </div>

        <div class="tio-beta-nav__groups">

          {{! Home sits on its own, above the labelled groups. }}
          <BetaSideNavGroup>
            <BetaSideNavLink
              @route="authenticated.dashboard"
              @icon={{PhHouse}}
              @label={{t "sidebar.home"}}
              @matchSubtree={{false}}
              data-test-beta-side-nav-home
            />
          </BetaSideNavGroup>

          {{! ── Note 8 — Administration ──
               "These are links both internal and Partner Admin will need."
               The design shows four rows; the previous rail exposed six behind
               finer-grained gates. All six are kept — the gates are copied
               verbatim so nobody's nav loses an entry they had. }}
          {{#if this.showAdministration}}
            <BetaSideNavGroup @title={{t "sidebar.administration"}}>
              {{#if this.showAdminCore}}
                <BetaSideNavLink
                  @route="authenticated.admin.employees"
                  @icon={{PhUsersThree}}
                  @label={{t "sidebar.employees"}}
                  data-test-beta-side-nav-admin-employees
                />
                <BetaSideNavLink
                  @route="authenticated.admin.eligibility-files"
                  @icon={{PhFileArrowUp}}
                  @label={{t "sidebar.eligibility_files"}}
                  data-test-beta-side-nav-admin-eligibility-files
                />
              {{/if}}
              {{#if this.showAdminTas}}
                <BetaSideNavLink
                  @route="authenticated.admin.tas.approval-queue"
                  @icon={{PhStudent}}
                  @label={{t "sidebar.approval_queue"}}
                  data-test-beta-side-nav-admin-tas-approval-queue
                />
              {{/if}}
              {{#if this.showAdminPslf}}
                <BetaSideNavLink
                  @route="authenticated.admin.pslf.forms"
                  @icon={{PhHandHeart}}
                  @label={{t "sidebar.admin_pslf"}}
                  data-test-beta-side-nav-admin-pslf
                />
              {{/if}}

              {{! Below: rows the previous rail exposed that the design does not
                  name. Kept so no admin loses a destination. }}
              {{#if this.showAdminCore}}
                <BetaSideNavLink
                  @route="authenticated.admin.reports"
                  @icon={{PhGridNine}}
                  @label={{t "common.reporting.default"}}
                  data-test-beta-side-nav-admin-reports
                />
              {{/if}}
              {{#if this.showAdminTasPayments}}
                <BetaSideNavLink
                  @route="authenticated.admin.tuition-assistance.payments"
                  @icon={{PhStudent}}
                  @label={{t "sidebar.payments"}}
                  data-test-beta-side-nav-admin-tuition-assistance-payments
                />
              {{/if}}
              {{#if this.showAllReports}}
                <BetaSideNavLink
                  @route="authenticated.admin.reporting.index"
                  @icon={{PhGridNine}}
                  @label={{t "reports.all_reports_link"}}
                  data-test-beta-side-nav-admin-tuition-assistance-reports
                />
              {{/if}}
            </BetaSideNavGroup>
          {{/if}}

          {{! ── Benefits ──
               The design names five rows, in this order. Everything below the
               marked divide is a destination the previous rail exposed that the
               design does not name; kept so no partner configuration loses a
               link. }}
          <BetaSideNavGroup @title={{t "sidebar.benefits"}}>
            {{#if this.showTuitionAssistanceRow}}
              <BetaSideNavLink
                @route="authenticated.tas.dashboard"
                @icon={{PhGraduationCap}}
                @label={{t "sidebar.tuition_assistance"}}
                data-test-beta-side-nav-employee-primary-tuition-assistance
              />
            {{/if}}
            {{#if this.showWellnessRow}}
              <BetaSideNavLink
                @route="authenticated.wellness-insights"
                @icon={{PhFirstAidKit}}
                @label={{t "sidebar.loan_wellness"}}
                data-test-beta-side-nav-loan-insights
              />
            {{/if}}
            {{#if this.showPslfRow}}
              <BetaSideNavLink
                @route="authenticated.pslf.dashboard"
                @icon={{PhLifebuoy}}
                @label={{t "sidebar.loan_forgiveness"}}
                data-test-beta-side-nav-pslf
              />
            {{/if}}
            {{#if this.showSlrRow}}
              <BetaSideNavLink
                @route="authenticated.slr.dashboard"
                @icon={{PhHandCoins}}
                @label={{t "sidebar.loan_repayment"}}
                data-test-beta-side-nav-employee-employer-contributions
              />
            {{/if}}
            {{#if this.showLearningPartners}}
              <BetaSideNavLink
                @route="authenticated.lpn.index"
                @icon={{PhGlobe}}
                @label={{t "sidebar.learning_partners"}}
              />
            {{/if}}

            {{! ── not named by the design ── }}
            {{#if this.isMember}}
              <BetaSideNavLink
                @route="authenticated.account-activity.accounts"
                @icon={{PhUserCircle}}
                @label={{t "sidebar.loan_summary"}}
                data-test-beta-side-nav-employee-student-loan-summary
              />
            {{/if}}
            {{#if this.showSyfRow}}
              <BetaSideNavLink
                @route="authenticated.syf.dashboard"
                @icon={{PhRocketLaunch}}
                @label={{t "sidebar.secure_your_future"}}
                data-test-beta-side-nav-employee-primary-secure-your-future
              />
            {{/if}}
            {{#if this.showIdrRow}}
              <BetaSideNavLink
                @route="authenticated.idr.dashboard"
                @icon={{PhCurrencyCircleDollar}}
                @label={{t "sidebar.income_driven_repayment"}}
                data-test-beta-side-nav-employee-primary-income-driven-repayment
              />
            {{/if}}
          </BetaSideNavGroup>

          {{! ── Resources ── design's four first, then unnamed extras. }}
          <BetaSideNavGroup @title={{t "sidebar.resources"}}>
            {{#if this.isMember}}
              <BetaSideNavLink
                @route="authenticated.repaying-student-debt.coaching"
                @icon={{PhMegaphone}}
                @label={{t "sidebar.coaching"}}
              />
            {{/if}}
            {{#if this.showMemberOnly}}
              <BetaSideNavLink
                @route={{FAQS_ROUTE}}
                @icon={{PhBookOpenText}}
                @label={{t "sidebar.faqs"}}
              />
            {{/if}}
            {{! Rail rows until the opt-in moves them to the avatar menu. }}
            {{#unless @useAvatarMenu}}
              {{#if @isZendeskEnabled}}
                <BetaSideNavLink
                  @route="authenticated.contact-us"
                  @icon={{PhHeadset}}
                  @label={{t "sidebar.support"}}
                  @onClick={{@onOpenChat}}
                />
              {{else}}
                <BetaSideNavLink
                  @route="authenticated.contact-us"
                  @icon={{PhHeadset}}
                  @label={{t "sidebar.support"}}
                />
              {{/if}}
              <BetaSideNavLink
                @route="authenticated.contact-us"
                @icon={{PhQuestionMark}}
                @label={{t "sidebar.help"}}
              />
            {{/unless}}

            {{! ── not named by the design ──
                 The forgiveness GUIDE at
                 `repaying-student-debt.loan-forgiveness.index` used to sit here.
                 It shares the `sidebar.loan_forgiveness` label with the PSLF
                 dashboard in Benefits above, which the old nav got away with
                 because it was nested under "Repaying Debt" — this rail is flat,
                 so the same words appeared twice with different destinations.
                 Dropped rather than relabelled: inventing copy is design's call.
                 See BETA_SIDE_NAV_GAPS.md (gap: route-coverage). }}
            {{#if this.isMember}}
              <BetaSideNavLink
                @route="authenticated.repaying-student-debt.repayment-strategy-finder"
                @icon={{PhTarget}}
                @label={{t "sidebar.strategy_finder"}}
              />
            {{/if}}
            {{#if this.showMemberOnly}}
              <BetaSideNavLink
                @route="authenticated.repaying-student-debt.repayment-options"
                @icon={{PhScales}}
                @label={{t "sidebar.consolidation_refinancing"}}
              />
              {{#unless @showTuitionAssistance}}
                <BetaSideNavLink
                  @route="authenticated.college-planning-mentors"
                  @icon={{PhUsersThree}}
                  @label={{t "sidebar.education_mentors"}}
                />
              {{/unless}}
              <BetaSideNavLink
                @route="authenticated.questions-you-have.repayment-plans"
                @icon={{PhBookOpen}}
                @label={{t "sidebar.repayment_plans"}}
              />
              <BetaSideNavLink
                @route="authenticated.questions-you-have.glossary"
                @icon={{PhBookOpen}}
                @label={{t "sidebar.loan_glossary"}}
              />
            {{/if}}
          </BetaSideNavGroup>

        </div>
      </div>

      {{! ── Note 6 — user pill, pinned to the bottom ── }}
      <BetaSideNavUser
        @isZendeskEnabled={{@isZendeskEnabled}}
        @onOpenChat={{@onOpenChat}}
        @tagline={{@tagline}}
        @brand={{this.brand}}
        @useAvatarMenu={{@useAvatarMenu}}
      />
    </nav>
  </template>
}
